import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { isEmpty, isUndefined } from 'lodash';
import { KeycloakLogin } from 'src/types/keycloak_login';
import { User } from 'src/types/user';
import parse from 'src/utils/parse';
import {
  extractAuthTokens,
  persistAuthTokens,
  type AuthTokens,
} from 'src/utils/authTokens';
import { userApi } from './userApi';
import logger from 'src/utils/logger';
import { persistWhiteLabelOrganization } from 'src/utils/whiteLabel';

class AuthApi {
  async login({ email, password }): Promise<KeycloakLogin> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${process.env.REACT_APP_CASES}/user/login`, {
          username: email,
          password,
        })
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            const data = response?.data;
            resolve(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async reset(keycloack, key, password): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .put(
          `${process.env.REACT_APP_CASES}/user/password/reset/${keycloack}?key=${key}`,
          {
            password,
          }
        )
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          }
        })
        .catch((error) => {
          reject(
            error?.response?.data?.message ||
              error?.message ||
              'Unable to reset password'
          );
        });
    });
  }

  async forgetPassword(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `${process.env.REACT_APP_CASES}/user/password/forget?email=${email}`
        )
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async register(): Promise<void> {
    logger('register');
  }

  me(accessToken): Promise<User> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        // Decode access token
        const decoded: any = jwtDecode(accessToken);
        if (!decoded) {
          reject(new Error('Invalid authorization token'));
          return;
        }
        const userByKeyCloak: any = await userApi.getByKeycloakId();
        if (!isEmpty(userByKeyCloak?.organization)) {
          persistWhiteLabelOrganization(userByKeyCloak.organization);
        }

        if (userByKeyCloak) {
          resolve({
            userKeyCloakId: decoded?.sub,
            firstName: userByKeyCloak.firstName,
            lastName: userByKeyCloak.lastName,
            designation: userByKeyCloak.designation,
            phoneNumber: userByKeyCloak.phoneNumber,
            id: userByKeyCloak?.id,
            name: `${userByKeyCloak.firstName} ${userByKeyCloak.lastName}`,
            email: decoded.email,
            image: userByKeyCloak.image,
            organizationId: userByKeyCloak?.organizationId,
            organization: userByKeyCloak?.organization,
            permissions: userByKeyCloak?.permissions,
            userKafkaTopic: userByKeyCloak?.userKafkaTopic,
            notifications: userByKeyCloak?.notifications?.list,
            role: userByKeyCloak?.role,
            language: userByKeyCloak?.language,
            description: userByKeyCloak.description,
          });
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  async refreshSession(refreshToken: string): Promise<AuthTokens> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${process.env.REACT_APP_CASES}/user/refresh-token`, {
          refreshToken,
        })
        .then((response) => {
          const res: any = parse(response);
          const tokens = extractAuthTokens(res);

          if (!tokens) {
            reject(new Error('Invalid refresh token response'));
            return;
          }

          persistAuthTokens(tokens);
          resolve(tokens);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async verifyTwoFactorCode({ email, password, otp }): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${process.env.REACT_APP_CASES}/user/auth/verify-otp`, {
          email,
          password,
          otp,
        })
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            const data = response?.data;
            resolve(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}

export const authApi = new AuthApi();
